import { useEffect, useState } from "react";
import {
  Database, Download, RefreshCw, Copy, CheckCircle2, AlertTriangle, ExternalLink, Clock, Loader2, Wrench,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Tally Prime integration setup page.
 *
 * Shows:
 *  - The user's long-lived sync token (one-click copy)
 *  - "Download rocvault.tdl" button — file has the token pre-baked in
 *  - 6-step install guide
 *  - Latest sync banner (if any) with download workbook link
 *  - Recent attempts diagnostic panel for debugging
 */
export default function TallyIntegration() {
  const [token, setToken] = useState(null);
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [showDiag, setShowDiag] = useState(false);

  const loadEverything = async () => {
    try {
      const [t, l] = await Promise.all([
        axios.get(`${API}/integrations/tally/token`, { withCredentials: true }),
        axios.get(`${API}/integrations/tally/latest`, { withCredentials: true }),
      ]);
      setToken(t.data.token);
      setLatest(l.data && l.data.job_id ? l.data : null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Tally integration");
    }
  };

  const loadAttempts = async () => {
    try {
      const r = await axios.get(`${API}/integrations/tally/recent-attempts`, { withCredentials: true });
      setAttempts(r.data.attempts || []);
    } catch {
      setAttempts([]);
    }
  };

  useEffect(() => { loadEverything(); }, []);

  const handleCopy = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => toast.success("Token copied"));
  };

  const handleRotate = async () => {
    if (!confirm("Rotate the token? Your existing rocvault.tdl will stop working until you re-download and re-install it.")) return;
    setRotating(true);
    try {
      const r = await axios.post(`${API}/integrations/tally/token/rotate`, {}, { withCredentials: true });
      setToken(r.data.token);
      toast.success("Token rotated — please re-download the TDL file");
    } catch (e) {
      toast.error("Rotation failed");
    } finally {
      setRotating(false);
    }
  };

  const handleDownloadTdl = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/integrations/tally/install.tdl`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rocvault.tdl";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("rocvault.tdl downloaded. Follow Step 2 onwards below.");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadSyncScript = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/integrations/tally/sync-script-download`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rocvault_sync.zip";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("rocvault_sync.zip downloaded — extract it and double-click rocvault_sync.bat in Tally.");
    } catch (e) {
      toast.error("Download failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tally-integration-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
        <Database size={14} className="text-[#002FA7]" /> Integrations · Tally Prime
      </div>
      <h1 className="font-semibold text-2xl sm:text-3xl tracking-tight text-zinc-950">
        Push Tally Prime data to ROC Vault — one click
      </h1>
      <p className="mt-2 text-sm text-zinc-600 max-w-3xl">
        One-click sync from Tally Prime to ROC Vault. The script uses Tally's built-in XML
        server (already enabled by default) and forwards your Trial Balance to ROC Vault via
        Windows' native HTTPS — bypassing the quirks of Tally's TDL HTTP layer entirely.
        Auto-classified into Schedule III Division I, ready as a 7-sheet workbook.
      </p>

      {/* ============================================================
          v3.0 ONE-CLICK SYNC SCRIPT — Hero (Recommended path)
          ============================================================ */}
      <div className="mt-6 border-2 border-[#002FA7] bg-white p-6" data-testid="tally-v3-hero">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#002FA7] font-semibold">
          <CheckCircle2 size={12} /> Recommended · v3.0 One-Click Sync
        </div>
        <h2 className="mt-1 font-semibold text-xl text-zinc-950 tracking-tight">
          Download • Double-click • Done.
        </h2>
        <p className="mt-2 text-sm text-zinc-700 max-w-3xl">
          A small ZIP with a PowerShell script + double-click launcher. No install, no admin
          rights, no Python — works with any Tally Prime version (2.x and newer). The script
          pulls the Trial Balance from Tally on <code className="bg-zinc-50 px-1 text-[11px] border border-zinc-200">localhost:9000</code>,
          forwards to ROC Vault, and opens your workbook in the browser. Total time: 5-15 seconds.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleDownloadSyncScript}
            disabled={busy || !token}
            data-testid="tally-download-sync-script"
            className="px-5 py-3 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#001E70] disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Download rocvault_sync.zip
          </button>
          <div className="text-[11px] text-zinc-500 leading-relaxed self-center max-w-xs">
            ~4 KB ZIP. Your token is baked in. If you rotate the token, re-download this.
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="tally-v3-steps">
          <div className="border border-zinc-200 bg-zinc-50/50 px-3 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-1">Step 1 · Extract</div>
            <div className="text-xs text-zinc-700">Unzip <code className="text-[10px]">rocvault_sync.zip</code> anywhere — Desktop, Documents, anywhere.</div>
          </div>
          <div className="border border-zinc-200 bg-zinc-50/50 px-3 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-1">Step 2 · Open Tally</div>
            <div className="text-xs text-zinc-700">Open Tally Prime, load the company you want to push. Leave it running.</div>
          </div>
          <div className="border border-zinc-200 bg-zinc-50/50 px-3 py-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium mb-1">Step 3 · Double-click</div>
            <div className="text-xs text-zinc-700">Double-click <code className="text-[10px]">rocvault_sync.bat</code>. Wait 5-15s for SUCCESS.</div>
          </div>
        </div>

        <details className="mt-4 text-xs text-zinc-600" data-testid="tally-v3-prereqs">
          <summary className="cursor-pointer hover:text-zinc-900 font-medium">Prerequisites &amp; troubleshooting</summary>
          <div className="mt-2 space-y-2 leading-relaxed pl-2 border-l-2 border-zinc-100">
            <div><b>Tally HTTP server:</b> Already enabled by default in Tally Prime. If disabled, go to <kbd className="font-mono text-[10px] bg-white border border-zinc-300 px-1">F1</kbd> → Settings → Connectivity → Tally is acting as: <b>Server</b> / <b>Both</b>, Port: <b>9000</b>.</div>
            <div><b>"Could not reach Tally on localhost:9000":</b> Tally not open, company not loaded, or HTTP server disabled. See above.</div>
            <div><b>"Invalid token":</b> You rotated the token after downloading. Re-download this ZIP.</div>
            <div><b>"FAILED at Step 2":</b> Windows Firewall blocking outbound HTTPS to rocvault.in. Allow PowerShell through the firewall, or add an exception for the script.</div>
            <div><b>Want to see what's in the script before running?</b> Open <code>rocvault_sync.ps1</code> in Notepad — it's plain text, ~60 lines, no obfuscation.</div>
          </div>
        </details>
      </div>

      {/* Latest sync banner */}
      {latest && (
        <div className="mt-6 border border-blue-200 bg-blue-50/40 px-4 py-3" data-testid="tally-latest-sync">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-[#002FA7] font-medium">
            <CheckCircle2 size={12} /> Latest Tally sync
          </div>
          <div className="mt-1 grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs text-zinc-700">
            <div><span className="text-zinc-500">Company: </span><b>{latest.company_name || "—"}</b></div>
            <div><span className="text-zinc-500">Ledgers: </span><b>{latest.row_count}</b></div>
            <div><span className="text-zinc-500">Pushed: </span><b>{new Date(latest.pushed_at).toLocaleString("en-IN")}</b></div>
            <div>
              <a
                href={`${API}/integrations/tally/download/${latest.job_id}`}
                data-testid="tally-download-latest"
                className="text-[#002FA7] font-medium inline-flex items-center gap-1"
                download
              >
                <Download size={11} /> Download workbook
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          Token management — used by BOTH paths (v3 script + TDL)
          ============================================================ */}
      <div className="mt-6 border border-zinc-200 bg-white p-6">
        <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-2">
          Your Tally sync token
        </div>
        <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 font-mono text-xs select-all break-all" data-testid="tally-token-display">
          {token || <span className="text-zinc-400">Loading…</span>}
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleCopy}
            disabled={!token}
            data-testid="tally-copy-token"
            className="px-3 py-2 border border-zinc-200 text-xs font-medium hover:border-[#002FA7] flex items-center gap-1.5 disabled:opacity-50"
          >
            <Copy size={11} /> Copy
          </button>
          <button
            onClick={handleRotate}
            disabled={rotating}
            data-testid="tally-rotate-token"
            className="px-3 py-2 border border-zinc-200 text-xs font-medium hover:border-[#002FA7] flex items-center gap-1.5 disabled:opacity-50"
          >
            {rotating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Rotate
          </button>
        </div>
        <p className="mt-3 text-[11px] text-zinc-500">
          Keep this token private — anyone with it can push Trial Balance data into your account.
          Rotate immediately if you suspect compromise. After rotating you must re-download the
          sync ZIP (or TDL file, if you use that path) so it picks up the new token.
        </p>
      </div>

      {/* ============================================================
          Advanced — Legacy TDL path (collapsed by default)
          ============================================================ */}
      <details className="mt-6 border border-zinc-200 bg-white" data-testid="tally-tdl-section">
        <summary className="cursor-pointer px-6 py-4 flex items-center justify-between hover:bg-zinc-50 select-none">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-medium">Advanced · Legacy path</div>
            <div className="mt-0.5 text-sm font-semibold text-zinc-800">TDL add-on (in-Tally menu item)</div>
          </div>
          <div className="text-[11px] text-zinc-500">Click to expand</div>
        </summary>
        <div className="border-t border-zinc-100 px-6 py-5 space-y-5">
          <div className="text-xs text-zinc-600 bg-amber-50 border border-amber-200 px-3 py-2">
            <b className="text-amber-900">Note:</b> The TDL path adds a "Push to ROC Vault" menu inside
            Tally Gateway. Convenient when it works, but Tally's TDL HTTP POST has subtle
            version-specific bugs. If you hit silent failures here, switch to the v3.0 one-click
            script above — it's more reliable.
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-2">
              Step 1 — Download the TDL add-on
            </div>
            <p className="text-sm text-zinc-700">
              The file <code className="bg-zinc-50 px-1.5 py-0.5 text-[11px] border border-zinc-200">rocvault.tdl</code> is
              pre-baked with your token. Save it anywhere on the PC running Tally Prime.
            </p>
            <button
              onClick={handleDownloadTdl}
              disabled={busy}
              data-testid="tally-download-tdl"
              className="mt-3 px-4 py-2.5 border border-[#002FA7] text-[#002FA7] text-sm font-medium hover:bg-blue-50/60 disabled:opacity-50 flex items-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Download rocvault.tdl
            </button>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-3">
              Step 2 — Load the TDL inside Tally Prime (one time)
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700">
              <li>Open <b>Tally Prime</b>.</li>
              <li>From the Gateway of Tally press <kbd className="font-mono text-[11px] bg-zinc-100 border border-zinc-200 px-1.5">F1</kbd> (Help) → <b>TDL &amp; Add-On</b>.</li>
              <li>Press <kbd className="font-mono text-[11px] bg-zinc-100 border border-zinc-200 px-1.5">F4</kbd> (Manage Local TDLs).</li>
              <li>Set <b>Load TDL files on startup</b> = <b>Yes</b>.</li>
              <li>In <b>List of TDL files</b>, paste the full path to <code className="bg-zinc-50 px-1.5 py-0.5 text-[11px] border border-zinc-200">rocvault.tdl</code> (e.g. <code>C:\Users\You\Documents\rocvault.tdl</code>).</li>
              <li>Press <kbd className="font-mono text-[11px] bg-zinc-100 border border-zinc-200 px-1.5">Ctrl + A</kbd> to accept. <b>Restart Tally</b>.</li>
            </ol>

            <div className="mt-5 text-[11px] uppercase tracking-[0.14em] text-zinc-600 font-medium mb-3">
              Step 3 — Push your TB (every time you want to sync)
            </div>
            <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700">
              <li>Open the Tally company you want to push.</li>
              <li>Press <kbd className="font-mono text-[11px] bg-zinc-100 border border-zinc-200 px-1.5">Esc</kbd> to reach the Gateway of Tally.</li>
              <li>You'll see a new menu item <b>"Push to ROC Vault"</b> — click it (or press <kbd className="font-mono text-[11px] bg-zinc-100 border border-zinc-200 px-1.5">R</kbd>).</li>
              <li>You'll see either a Success or Failure popup. If Failure, open Connection Diagnostics below.</li>
              <li>Open <a href="/schedule3" className="text-[#002FA7] font-medium inline-flex items-center gap-0.5">ROC Vault Schedule III page <ExternalLink size={11} /></a> — latest sync banner shows the workbook.</li>
            </ol>
          </div>
        </div>
      </details>

      {/* Troubleshooting */}
      <div className="mt-6 border border-amber-200 bg-amber-50/40 px-4 py-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-amber-700 font-medium">
          <AlertTriangle size={12} /> Troubleshooting
        </div>
        <ul className="mt-1 text-[12px] text-zinc-700 space-y-1 list-disc list-inside">
          <li><b>Menu item not appearing?</b> Check that Tally was restarted after loading the TDL file. Also confirm "Load TDL files on startup" is set to Yes.</li>
          <li><b>"Invalid token" error?</b> Re-download the TDL — you may have rotated the token but kept the old file.</li>
          <li><b>"Connection failed"?</b> Confirm the PC running Tally has internet access. Corporate firewalls sometimes block outgoing HTTPS to non-whitelisted domains — ask IT to allow rocvault.in.</li>
          <li><b>Wrong company synced?</b> The TDL pushes the <b>currently open</b> Tally company. Open the right company first, then click Push.</li>
          <li><b>Want partial-year sync?</b> Currently the add-on pushes the whole company TB from financial-year-beginning to the current date. Partial-period sync is on the roadmap.</li>
        </ul>
      </div>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-zinc-500">
        <Clock size={11} /> Reload this page or visit <a href="/schedule3" className="underline">/schedule3</a> after pushing to see your latest sync.
      </div>

      {/* Diagnostic panel — recent push attempts (server-side log) */}
      <div className="mt-6 border border-zinc-200 bg-white" data-testid="tally-diag-panel">
        <button
          type="button"
          onClick={() => {
            const next = !showDiag;
            setShowDiag(next);
            if (next) loadAttempts();
          }}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-50"
          data-testid="tally-diag-toggle"
        >
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-zinc-700 font-medium">
            <Wrench size={12} /> Connection diagnostics
          </div>
          <span className="text-[11px] text-zinc-500">{showDiag ? "Hide" : "Show"}</span>
        </button>

        {showDiag && (
          <div className="px-4 pb-4 border-t border-zinc-100">
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Backend URL Tally posts to</div>
                <div className="bg-zinc-50 border border-zinc-200 px-2.5 py-1.5 font-mono text-[11px] break-all" data-testid="tally-backend-url">
                  {BACKEND_URL}/api/schedule3/from-tally?token={token ? token.slice(0, 8) + "…" : "…"}
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">
                  This is the exact URL inside your <code>rocvault.tdl</code>. Tally's HTTP POST
                  must reach this URL from your PC. Test it via curl or your browser to confirm
                  network/TLS reach.
                </p>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 mb-1">Quick connectivity test</div>
                <div className="bg-zinc-50 border border-zinc-200 px-2.5 py-1.5 font-mono text-[10px] break-all">
                  curl -X POST "{BACKEND_URL}/api/schedule3/from-tally?token={token || 'YOUR_TOKEN'}" --data "&lt;ENVELOPE/&gt;"
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">
                  Run from the PC that has Tally installed. Expect <code>422</code> (empty envelope —
                  proves the URL is reachable). If you get a connection/TLS error instead, your
                  PC can't reach the server.
                </p>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                  Recent push attempts (server-side, last 20)
                </div>
                <button onClick={loadAttempts} className="text-[11px] text-[#002FA7] hover:underline" data-testid="tally-diag-refresh">
                  Refresh
                </button>
              </div>
              {attempts.length === 0 ? (
                <div className="text-xs text-zinc-500 border border-dashed border-zinc-200 px-3 py-6 text-center">
                  No attempts logged yet. Push from Tally — if nothing appears here, Tally's
                  request never reached the server (network/TLS issue on your PC).
                </div>
              ) : (
                <div className="border border-zinc-200 overflow-x-auto" data-testid="tally-attempts-table">
                  <table className="w-full text-[11px]">
                    <thead className="bg-zinc-50 border-b border-zinc-200">
                      <tr className="text-left">
                        <th className="px-2 py-1.5 font-medium text-zinc-600">When</th>
                        <th className="px-2 py-1.5 font-medium text-zinc-600">IP</th>
                        <th className="px-2 py-1.5 font-medium text-zinc-600">Token</th>
                        <th className="px-2 py-1.5 font-medium text-zinc-600">Bytes</th>
                        <th className="px-2 py-1.5 font-medium text-zinc-600">Status</th>
                        <th className="px-2 py-1.5 font-medium text-zinc-600">Rows</th>
                        <th className="px-2 py-1.5 font-medium text-zinc-600">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attempts.map((a, i) => (
                        <tr key={i} className="border-b border-zinc-100" data-testid={`tally-attempt-${i}`}>
                          <td className="px-2 py-1.5 text-zinc-700">{new Date(a.ts).toLocaleString("en-IN")}</td>
                          <td className="px-2 py-1.5 font-mono text-zinc-700">{a.ip || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-zinc-700">{a.token_prefix || "—"}</td>
                          <td className="px-2 py-1.5 text-zinc-700">{a.body_size}</td>
                          <td className={`px-2 py-1.5 font-medium ${a.status === "ok" ? "text-green-700" : "text-red-700"}`}>
                            {a.status}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-700">{a.rows_parsed || "—"}</td>
                          <td className="px-2 py-1.5 text-zinc-600 max-w-xs truncate" title={a.error}>{a.error || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
